/**
 * The stationary screens: Harta, Raportet e mia, Cilësimet, and the pin detail
 * sheet. None of them exist while the car is moving — the strip that opens them
 * only appears once the vehicle has stopped, and drive mode is otherwise the
 * whole app.
 */
import type maplibregl from "maplibre-gl";
import { HAZARDS, HAZARD_TYPES, type HazardType } from "../../shared/hazards.js";
import { clipUrl, type Alert } from "../../shared/alerts.js";
import { pinMark } from "../lib/marks.js";
import type { Session } from "../lib/session.js";
import type { VoiceAlerts } from "../lib/audio.js";
import { loadSettings, saveSettings, applyGlare, type Settings } from "../lib/settings.js";
import { shareAlert } from "../lib/share.js";

export type ScreenName = "harta" | "raportet" | "cilesimet";

export type ScreensDeps = {
  session: Session;
  voice: VoiceAlerts;
  getMap: () => maplibregl.Map | null;
  /** Everything the engine holds, unfiltered — Harta shows it all. */
  getAll: () => Alert[];
  /** main.ts switches the renderer between the drive corridor and the full set. */
  onBrowse: (on: boolean) => void;
  onEndDrive: () => void;
  vote: (alertId: string, vote: "confirm" | "reject") => Promise<boolean>;
};

type MineItem = {
  id: string; type: HazardType; status: "pending" | "published" | "retired";
  samples: number; confidence: number; createdAt: number; expiresAt: number;
  /** Filed by this device, or merged into someone else's report as a confirmation. */
  mine: "reported" | "confirmed"; at: number;
};

/** Every string that did not originate in this codebase goes through here. */
const esc = (v: unknown): string =>
  String(v).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (el === null) throw new Error(`missing element #${id}`);
  return el as T;
};

/** "para 12 min", "para 3 orë", "tani". */
export function relTime(ms: number, now = Date.now()): string {
  const d = Math.max(0, now - ms);
  if (d < 60_000) return "tani";
  const m = Math.round(d / 60_000);
  if (m < 60) return `para ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `para ${h} orë`;
  return `para ${Math.round(h / 24)} ditësh`;
}

function statusLabel(it: MineItem, now: number): string {
  const who = it.mine === "confirmed" ? "Konfirmuar nga ti" : "Raportuar nga ti";
  if (it.status === "retired") return `${who} · Hequr`;
  if (it.expiresAt < now) return `${who} · Skaduar`;
  if (it.status === "pending") return `${who} · Në pritje të konfirmimit`;
  return `${who} · Aktiv · ${it.samples} ${it.samples === 1 ? "shofer" : "shoferë"}`;
}

const isType = (v: unknown): v is HazardType =>
  typeof v === "string" && (HAZARD_TYPES as readonly string[]).includes(v);

export class Screens {
  private current: ScreenName | null = null;
  private settings: Settings = loadSettings();
  private detailAlert: Alert | null = null;

  constructor(private readonly deps: ScreensDeps) {
    applyGlare(this.settings.glare);
    this.deps.voice.setVolume(this.settings.volume);

    $("stripHarta").addEventListener("click", () => this.open("harta"));
    $("stripRaportet").addEventListener("click", () => this.open("raportet"));
    $("stripCilesimet").addEventListener("click", () => this.open("cilesimet"));
    $("screenClose").addEventListener("click", () => this.close());
    $("hartaClose").addEventListener("click", () => this.close());
    $("detailClose").addEventListener("click", () => this.hideDetail());
    $("detailConfirm").addEventListener("click", () => void this.castVote("confirm"));
    $("detailReject").addEventListener("click", () => void this.castVote("reject"));
    $("detailShare").addEventListener("click", () => {
      if (this.detailAlert !== null) void shareAlert(this.detailAlert);
    });
  }

  get isOpen(): boolean {
    return this.current !== null;
  }

  /** The strip is the only way in, and it exists only while stopped. */
  setStationary(on: boolean): void {
    $("strip").hidden = !on || this.current !== null;
  }

  open(name: ScreenName): void {
    this.current = name;
    $("strip").hidden = true;
    if (name === "harta") {
      this.openHarta();
      return;
    }
    const screen = $("screen");
    $("screenTitle").textContent = name === "raportet" ? "Raportet e mia" : "Cilësimet";
    screen.hidden = false;
    if (name === "raportet") void this.renderRaportet();
    else this.renderCilesimet();
  }

  close(): void {
    if (this.current === "harta") this.closeHarta();
    this.current = null;
    $("screen").hidden = true;
    this.hideDetail();
  }

  /* ---------------- Harta: the drive map, unlocked ---------------- */

  private openHarta(): void {
    const map = this.deps.getMap();
    $("drive").dataset["browse"] = "1";
    $("harta").hidden = false;
    if (map !== null) {
      map.dragPan.enable();
      map.scrollZoom.enable();
      map.touchZoomRotate.enable();
      map.doubleClickZoom.enable();
      map.setBearing(0);
    }
    this.deps.onBrowse(true);
  }

  private closeHarta(): void {
    const map = this.deps.getMap();
    delete $("drive").dataset["browse"];
    $("harta").hidden = true;
    // Pan and zoom stay enabled: drive mode has free look with auto-recentre.
    if (map !== null) map.touchZoomRotate.disableRotation();
    this.deps.onBrowse(false);
  }

  /* ---------------- pin detail sheet ---------------- */

  showDetail(alert: Alert): void {
    this.detailAlert = alert;
    const spec = HAZARDS[alert.type];
    $("detailSign").innerHTML = pinMark(alert.type, false);
    $("detailLabel").textContent = spec.label;
    const src =
      alert.source === "official" ? "Zyrtare" :
      alert.source === "bridge" ? "Nga grupet" :
      alert.source === "osm" ? "Nga harta" : "Nga shoferët";
    const parts = [relTime(alert.createdAt), src];
    if (alert.samples > 1) parts.push(`${alert.samples} shoferë`);
    const hold = alert.hold === true;
    if (hold) parts.push("pa konfirmuar");
    $("detailMeta").textContent = parts.join(" · ");
    $("detailSign").classList.toggle("detail__sign--hold", hold);
    // The hold band asks a question; a published report asks for a status.
    $("detailConfirm").textContent = hold ? "Saktë" : "Është aty";
    $("detailReject").textContent = hold ? "Gabim" : "Nuk është";
    $("detailShare").hidden = hold || typeof navigator.share !== "function";
    const txt = $("detailText");
    if (alert.sourceText !== null && alert.sourceText !== "") {
      txt.hidden = false;
      txt.innerHTML = `<q>${esc(alert.sourceText)}</q>`;
    } else {
      txt.hidden = true;
      txt.textContent = "";
    }
    $("detailVoted").hidden = true;
    $("detail").hidden = false;
  }

  hideDetail(): void {
    this.detailAlert = null;
    $("detail").hidden = true;
  }

  private async castVote(vote: "confirm" | "reject"): Promise<void> {
    const a = this.detailAlert;
    if (a === null) return;
    const ok = await this.deps.vote(a.id, vote);
    const v = $("detailVoted");
    v.textContent = ok ? "Faleminderit. Vota u regjistrua." : "Vota u ruajt dhe do të dërgohet.";
    v.hidden = false;
  }

  /* ---------------- Raportet e mia ---------------- */

  private async renderRaportet(): Promise<void> {
    const body = $("screenBody");
    const pending = this.deps.session.pendingCount;
    body.innerHTML =
      (pending > 0
        ? `<p class="screen__note">${pending} ${pending === 1 ? "raportim pret" : "raportime presin"} për t'u dërguar.</p>`
        : "") +
      `<p class="screen__note" id="mineState">Duke ngarkuar…</p><ul class="list" id="mineList"></ul>`;

    const res = await this.deps.session.signed("/v1/reports/mine", {});
    const state = $("mineState");
    if (res === null || res.status !== 200) {
      state.textContent = "Nuk u lidh me serverin. Raportet e tua janë të ruajtura.";
      return;
    }
    const items = ((res.json as { items?: unknown[] } | null)?.items ?? []).filter(
      (it): it is MineItem => typeof it === "object" && it !== null && isType((it as MineItem).type),
    );
    if (items.length === 0) {
      state.textContent = "Ende asnjë raportim. Fanari i parë është yti.";
      return;
    }
    state.hidden = true;
    const now = Date.now();
    const list = $("mineList");
    list.innerHTML = items.map((it) => `
      <li class="row" data-id="${esc(it.id)}">
        <div class="row__sign">${pinMark(it.type, false)}</div>
        <div class="row__main">
          <div class="row__title">${esc(HAZARDS[it.type].label)}</div>
          <div class="row__meta">${esc(relTime(it.at, now))} · ${esc(statusLabel(it, now))}</div>
        </div>
        ${it.mine === "reported" && it.status !== "retired" && now - it.createdAt < 24 * 3600_000
          ? `<button type="button" class="row__action" data-del="${esc(it.id)}">Fshi</button>`
          : ""}
      </li>`).join("");

    list.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        btn.disabled = true;
        btn.textContent = "…";
        const r = await this.deps.session.signed(`/v1/reports/${btn.dataset["del"]}`, {}, "DELETE");
        const row = btn.closest(".row") as HTMLElement | null;
        if (r !== null && r.status === 200 && row !== null) {
          row.classList.add("row--gone");
          const meta = row.querySelector(".row__meta");
          if (meta !== null) meta.textContent = "Hequr";
          btn.remove();
        } else {
          btn.disabled = false;
          btn.textContent = "Fshi";
        }
      });
    });
  }

  /* ---------------- Cilësimet ---------------- */

  private renderCilesimet(): void {
    const body = $("screenBody");
    const s = this.settings;
    body.innerHTML = `
      <section class="group">
        <h3 class="group__title">Zëri</h3>
        <label class="field">
          <span>Volumi</span>
          <input id="setVolume" type="range" min="0" max="100" step="5" value="${Math.round(s.volume * 100)}" />
        </label>
        <button type="button" class="btn-ghost btn-ghost--full" id="setTest">Provo zërin</button>
        <p class="screen__note">Paralajmërimet flasin vetëm për Kujdes dhe Rrezik. Muzika ulet vetë gjatë njoftimit.</p>
      </section>

      <section class="group">
        <h3 class="group__title">Ekrani</h3>
        <label class="field field--switch">
          <span>Mode glare<small>Vija më të trasha kur ka diell të fortë</small></span>
          <input id="setGlare" type="checkbox" ${s.glare ? "checked" : ""} />
        </label>
        <p class="screen__note">Ekrani nuk kthehet kurrë në të bardhë: natën një ekran i bardhë të verbon për disa minuta.</p>
      </section>

      <section class="group">
        <h3 class="group__title">Rruga</h3>
        <label class="field field--switch">
          <span>Pa rrugë me pagesë<small>Rruga e Kombit (Milot–Morinë)</small></span>
          <input id="setTolls" type="checkbox" ${s.avoidTolls ? "checked" : ""} />
        </label>
        <p class="screen__note">Kur ka më shumë se një rrugë të arsyeshme, Fanar t'i tregon të dyja para se të nisesh.</p>
      </section>

      <section class="group">
        <h3 class="group__title">Udhëtimi</h3>
        <button type="button" class="btn-ghost btn-ghost--full" id="setEnd">Mbaro udhëtimin</button>
      </section>

      <section class="group group--about">
        <h3 class="group__title">Rreth Fanar</h3>
        <p><strong>fanar</strong> nga Z.E Digital Tech · versioni 0.1</p>
        <p>Drita para kthesës. Shoferët shqiptarë paralajmërojnë njëri-tjetrin për rrezikun në rrugë.</p>
        <p class="screen__note">
          Harta: © Kontribuesit e OpenStreetMap (ODbL), pllakat nga OpenFreeMap. Kërkimi: Photon (komoot).
          Rrugët llogariten në telefonin tënd nga harta e OpenStreetMap.
          Vendndodhja përdoret vetëm gjatë udhëtimit dhe nuk ruhet në server — filtri "përpara teje"
          punon në telefonin tënd. Raportimet janë anonime. Asnjë llogari, asnjë numër telefoni.
        </p>
        <p class="screen__note">
          Kontroll trafiku është vetëm prani, si çdo rrezik tjetër: pa foto, pa targa, pa ridrejtim.
          Fanar nuk të ndihmon të shmangësh asgjë — të ndihmon të ngadalësosh në kohë.
        </p>
        <p><a class="link" href="/kushtet" target="_blank" rel="noopener">Kushtet e përdorimit dhe privatësia</a></p>
      </section>`;

    $("setVolume").addEventListener("input", (ev) => {
      const v = Number((ev.target as HTMLInputElement).value) / 100;
      this.settings = { ...this.settings, volume: v };
      this.deps.voice.setVolume(v);
      saveSettings(this.settings);
    });
    $("setTest").addEventListener("click", () => {
      this.deps.voice.speak(clipUrl("grope", "primary"), 0);
    });
    $("setGlare").addEventListener("change", (ev) => {
      const on = (ev.target as HTMLInputElement).checked;
      this.settings = { ...this.settings, glare: on };
      applyGlare(on);
      saveSettings(this.settings);
    });
    $("setTolls").addEventListener("change", (ev) => {
      this.settings = { ...this.settings, avoidTolls: (ev.target as HTMLInputElement).checked };
      saveSettings(this.settings);
    });
    $("setEnd").addEventListener("click", () => {
      this.close();
      this.deps.onEndDrive();
    });
  }
}
